import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import { PhotoIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface FormData {
  name_en: string;
  name_ar: string;
  category: string;
  default_unit_en: string;
  default_unit_ar: string;
  common_unit_ids: string[];
  always_available: boolean;
  image: string;
}

interface CategoryOption {
  _id: string;
  name_en: string;
  name_ar: string;
}

interface UnitOption {
  _id: string;
  name: { en: string; ar: string };
}

export default function IngredientForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [commonUnitDropdownValue, setCommonUnitDropdownValue] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name_en: '',
    name_ar: '',
    category: '',
    default_unit_en: '',
    default_unit_ar: '',
    common_unit_ids: [],
    always_available: false,
    image: '',
  });

  useEffect(() => {
    loadCategories();
    loadUnits();
  }, []);

  useEffect(() => {
    if (isEditing && id) loadIngredient();
  }, [id]);

  const loadCategories = async () => {
    try {
      const res = await api.get('/ingredients/categories');
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setCategories(data);
    } catch (_) {
      setCategories([]);
    }
  };

  const loadUnits = async () => {
    try {
      const res = await api.get('/admin/units', { params: { activeOnly: 'true' } });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setUnits(data);
    } catch (_) {
      setUnits([]);
    }
  };

  const selectedUnitId = units.length
    ? (units.find(
        (u) =>
          (u.name?.en ?? '') === formData.default_unit_en && (u.name?.ar ?? '') === formData.default_unit_ar
      )?._id ?? '')
    : '';

  const handleUnitChange = (unitId: string) => {
    const unit = units.find((u) => u._id === unitId);
    if (unit) {
      setFormData({
        ...formData,
        default_unit_en: unit.name?.en ?? '',
        default_unit_ar: unit.name?.ar ?? '',
      });
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'ingredients');
    fd.append('type', 'image');
    setUploadingImage(true);
    try {
      const response = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.success && response.data?.data?.url) {
        return response.data.data.url;
      }
      throw new Error('Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const url = await uploadImage(file);
      setFormData({ ...formData, image: url });
    } catch (err: any) {
      alert(err?.message || t('pantryIngredients.failedToUploadImage'));
    }
    e.target.value = '';
  };

  const loadIngredient = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/ingredients/${id}`);
      const u = res.data?.data ?? res.data;
      const catId = typeof u.category === 'object' && u.category?._id ? u.category._id : u.category;
      const commonUnits = (u.common_units ?? []) as { en?: string; ar?: string }[];
      const unitsRes = await api.get('/admin/units', { params: { activeOnly: 'true' } });
      const unitsList: UnitOption[] = Array.isArray(unitsRes.data) ? unitsRes.data : unitsRes.data?.data ?? [];
      const common_unit_ids = commonUnits
        .map((c) => unitsList.find((u) => (u.name?.en ?? '') === (c.en ?? '') && (u.name?.ar ?? '') === (c.ar ?? ''))?._id)
        .filter(Boolean) as string[];
      setFormData({
        name_en: u.name_en ?? '',
        name_ar: u.name_ar ?? '',
        category: catId ?? '',
        default_unit_en: u.default_unit?.en ?? '',
        default_unit_ar: u.default_unit?.ar ?? '',
        common_unit_ids,
        always_available: u.always_available === true,
        image: u.image ?? '',
      });
    } catch (err) {
      console.error(err);
      alert(t('pantryIngredients.failedToLoad'));
      navigate('/?tab=pantryIngredients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category) {
      alert(t('pantryIngredients.selectCategory'));
      return;
    }
    try {
      setLoading(true);
      const common_units = formData.common_unit_ids
        .map((unitId) => units.find((u) => u._id === unitId))
        .filter(Boolean)
        .map((u) => ({ en: u!.name?.en ?? '', ar: u!.name?.ar ?? '' }));
      const payload = {
        name_en: formData.name_en.trim(),
        name_ar: formData.name_ar.trim(),
        category: formData.category,
        default_unit: {
          en: formData.default_unit_en.trim() || 'unit',
          ar: formData.default_unit_ar.trim() || 'وحدة',
        },
        common_units: common_units.length ? common_units : undefined,
        always_available: formData.always_available,
        image: formData.image.trim() || undefined,
      };
      if (isEditing && id) {
        await api.patch(`/ingredients/${id}`, payload);
      } else {
        await api.post('/ingredients', payload);
      }
      navigate('/?tab=pantryIngredients');
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || t('pantryIngredients.failedToUpdate'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <DashboardLayout title={t('common.loading')}>
        <div className="flex items-center justify-center min-h-[300px]">{t('pantryIngredients.loading')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isEditing ? t('pantryIngredients.editTitle') : t('pantryIngredients.createTitle')}
      subtitle={t('pantryIngredients.subtitle')}
    >
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-4">
          {/* Icon / image first – upload and it appears in the ingredients table */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryIngredients.icon')}</label>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex flex-col items-center gap-2">
                {formData.image ? (
                  <>
                    <img
                      src={formData.image}
                      alt="Ingredient"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300 bg-gray-100"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: '' })}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      {t('pantryIngredients.removeImage')}
                    </button>
                  </>
                ) : (
                  <div className="w-32 h-32 rounded-lg border border-gray-300 bg-gray-100 flex items-center justify-center">
                    <PhotoIcon className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageFileChange}
                />
                <button
                  type="button"
                  disabled={uploadingImage}
                  onClick={() => imageInputRef.current?.click()}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {uploadingImage ? t('common.uploading') : t('pantryIngredients.uploadImage')}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('pantryIngredients.iconHelp')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryIngredients.nameEn')}</label>
            <input
              type="text"
              required
              value={formData.name_en}
              onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryIngredients.nameAr')}</label>
            <input
              type="text"
              value={formData.name_ar}
              onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryIngredients.category')}</label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">{t('pantryIngredients.selectCategory')}</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id}>{c.name_en} / {c.name_ar}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryIngredients.defaultUnit')}</label>
            <select
              value={selectedUnitId}
              onChange={(e) => handleUnitChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">{t('pantryIngredients.selectUnit')}</option>
              {units.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name?.en ?? ''} / {u.name?.ar ?? ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pantryIngredients.commonUnits')}</label>
            <div className="flex gap-2">
              <select
                value={commonUnitDropdownValue}
                onChange={(e) => setCommonUnitDropdownValue(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">{t('pantryIngredients.selectUnit')}</option>
                {units
                  .filter((u) => !formData.common_unit_ids.includes(u._id))
                  .map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name?.en ?? ''} / {u.name?.ar ?? ''}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                disabled={!commonUnitDropdownValue}
                onClick={() => {
                  if (commonUnitDropdownValue && !formData.common_unit_ids.includes(commonUnitDropdownValue)) {
                    setFormData({ ...formData, common_unit_ids: [...formData.common_unit_ids, commonUnitDropdownValue] });
                    setCommonUnitDropdownValue('');
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
              >
                <PlusIcon className="w-5 h-5" />
                {t('common.add')}
              </button>
            </div>
            {formData.common_unit_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.common_unit_ids.map((unitId) => {
                  const u = units.find((x) => x._id === unitId);
                  return (
                    <span
                      key={unitId}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-sm"
                    >
                      {u ? `${u.name?.en ?? ''} / ${u.name?.ar ?? ''}` : unitId}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            common_unit_ids: formData.common_unit_ids.filter((id) => id !== unitId),
                          })
                        }
                        className="hover:bg-gray-200 rounded p-0.5"
                        aria-label={t('common.delete')}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/?tab=pantryIngredients')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
